import { Transition } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import {
  CheckboxInput,
  LoadingOrError,
  SubmitButton,
} from '@retainerclub/common';
import { usePolicy, useReferral } from '@retainerclub/hooks';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  MARKETING_LABEL,
  MarketingDescription,
  PREFERENCES_FOOTER,
  SELECT_ALL_LABEL,
  SMS_LABEL,
  SmsDescription,
  TERMS_LABEL,
  TermsDescription,
} from '../../AccountConsent/ConsentCopy';
import OnboardingFormAccount from './OnboardingFormAccount/OnboardingFormAccount';
import OnboardingFormAddress from './OnboardingFormAddress/OnboardingFormAddress';
import OnboardingFormErrors from './OnboardingFormErrors';
import OnboardingFormPatient from './OnboardingFormPatient/OnboardingFormPatient';
import OnboardingFormPayment from './OnboardingFormPayment/OnboardingFormPayment';
import OnboardingFormStep from './OnboardingFormStep';
import OnboardingFormSubscriptions from './OnboardingFormSubscriptions/OnboardingFormSubscriptions';

// Every plan on the account has been agreed to. Decides whether the payment
// step unlocks; the schema and the server enforce the same rule at submit.
function allAcknowledged(disclosure, acknowledgments) {
  if (disclosure?.required !== true) {
    return true;
  }

  const items = disclosure?.items || [];
  const submitted = acknowledgments || [];

  return (
    submitted.length === items.length &&
    submitted.every((acknowledgment) => acknowledgment?.agreed === true)
  );
}

function OnboardingFormInner({
  error = null,
  isReferralAlreadyClaimed = false,
  isReferralUnavailable = false,
  loading,
  mutationErrors = null,
  pError = null,
  rError = null,
}) {
  const [show, setShow] = useState(false);
  const { referral } = useReferral();
  const { setPolicyAndOpenModal } = usePolicy();

  const {
    formState: { errors, isLoading, isSubmitting, isValid, touchedFields },
    watch,
    trigger,
    setValue,
  } = useFormContext();

  // Watch all form values to ensure isValid updates when values change
  watch();

  // Watch the T&C checkbox specifically to trigger validation immediately when checked
  const acceptTerms = watch('userAttributes.acceptTerms');
  const acceptSms = watch('userAttributes.acceptSms');
  const acceptMarketing = watch('userAttributes.acceptMarketing');
  const allConsentsChecked =
    acceptTerms === true && acceptSms === true && acceptMarketing === true;

  // "Select all" checks the three consent boxes at once. consentSelectAll
  // records that they were checked that way and is sent with the form as
  // ledger evidence; it only holds while all three stay checked.
  function toggleAllConsents() {
    const next = !allConsentsChecked;
    const options = { shouldDirty: true, shouldTouch: true, shouldValidate: true };
    setValue('userAttributes.acceptTerms', next, options);
    setValue('userAttributes.acceptSms', next, options);
    setValue('userAttributes.acceptMarketing', next, options);
    setValue('consentSelectAll', next);
  }

  useEffect(() => {
    if (!allConsentsChecked) {
      setValue('consentSelectAll', false);
    }
  }, [allConsentsChecked, setValue]);

  // Subscription disclosure: one section per plan on the account, shown
  // before payment so billing details are only collected after the customer
  // has seen and accepted the subscription terms.
  const disclosure = referral?.subscriptionDisclosure;
  const disclosureRequired = disclosure?.required === true;
  const disclosureAcknowledged = allAcknowledged(
    disclosure,
    watch('subscriptionAcknowledgments'),
  );
  // Payment is collected for a free-trial referral, and for any account
  // whose pending plans charge soon after the first shipment (partner
  // paid-in-office free trials added before the holder onboarded).
  const paymentRequired =
    referral?.isFreeTrial === true ||
    disclosure?.requiresPaymentMethod === true;
  const subscriptionStep = 3;
  const paymentStep = disclosureRequired ? 4 : 3;
  const addressStep = paymentStep + (paymentRequired ? 1 : 0);

  // Trigger validation for T&C checkbox immediately when it's checked
  useEffect(() => {
    if (acceptTerms === true) {
      trigger('userAttributes.acceptTerms');
    }
  }, [acceptTerms, trigger]);

  // Determine if form is actually valid
  // With mode: 'onBlur', isValid only becomes true after all fields are blurred
  // But if there are no errors and fields have been touched, the form is likely valid
  const isFormValid = useMemo(() => {
    // If isValid is true, form is definitely valid
    if (isValid) {
      return true;
    }
    // If there are errors, form is not valid
    if (errors && Object.keys(errors).length > 0) {
      return false;
    }
    // If no fields have been touched yet, form is not valid
    if (!touchedFields || Object.keys(touchedFields).length === 0) {
      return false;
    }
    // If fields have been touched and there are no errors, form is likely valid
    // This handles the case where isValid is false due to mode: 'onBlur' but form is actually valid
    return true;
  }, [isValid, errors, touchedFields]);

  const masterLoading = useMemo(
    () => loading || isLoading || isSubmitting,
    [isLoading, isSubmitting, loading],
  );

  const shouldRender = LoadingOrError({
    loading: masterLoading,
    error: error || rError || pError,
    data: { hasData: true },
    dataKey: 'hasData',
    title: '',
  });

  const referralAlreadyClaimedMessage =
    'This referral link has already been used. To update your information, sign in and go to your account dashboard.';

  const referralUnavailableMessage =
    'This referral link is no longer active. Please contact your dental provider for an updated invitation.';

  return (
    <>
      {shouldRender === true ? (
        <>
          {isReferralAlreadyClaimed && (
            <div
              className="flex items-center gap-3 rounded-lg border border-red-500 bg-red-50 p-3 font-medium text-base text-red-800"
              role="alert"
            >
              <ExclamationTriangleIcon
                aria-hidden
                className="size-8 shrink-0 text-red-600"
              />
              <p className="text-center">{referralAlreadyClaimedMessage}</p>
              <ExclamationTriangleIcon
                aria-hidden
                className="size-8 shrink-0 text-red-600"
              />
            </div>
          )}
          {isReferralUnavailable && (
            <div
              className="flex items-center gap-3 rounded-lg border border-red-500 bg-red-50 p-3 font-medium text-base text-red-800"
              role="alert"
            >
              <ExclamationTriangleIcon
                aria-hidden
                className="size-8 shrink-0 text-red-600"
              />
              <p className="text-center">{referralUnavailableMessage}</p>
              <ExclamationTriangleIcon
                aria-hidden
                className="size-8 shrink-0 text-red-600"
              />
            </div>
          )}
          <OnboardingFormStep
            step={1}
            show
            title="Patient Information"
            description="Verify the person receiving retainers and let us help you get
          the perfect fit."
          >
            <OnboardingFormPatient setShow={setShow} show={show} />
          </OnboardingFormStep>
          <OnboardingFormStep
            step={2}
            title="Account Holder Details"
            description="Let's create your Retainer Club account to get started today."
            show={show}
          >
            <OnboardingFormAccount setShow={setShow} show={show} />
          </OnboardingFormStep>
          {disclosureRequired && (
            <OnboardingFormStep
              step={subscriptionStep}
              title="Your Plan, Explained"
              description="Before we continue, here is exactly what each plan on your account turns into and when it renews. Please review and confirm each one."
              show={show}
            >
              <OnboardingFormSubscriptions disclosure={disclosure} />
            </OnboardingFormStep>
          )}

          {paymentRequired && (
            <OnboardingFormStep
              step={paymentStep}
              title="Payment Information"
              description={
                disclosureAcknowledged
                  ? 'There is no charge today. We keep a card on file to complete your registration; it is only charged for the subscription renewals described above and for any purchases you choose to make later.'
                  : 'Confirm your plan above to unlock this step. There is no charge today. We keep a card on file to complete your registration; it is only charged for the subscription renewals described above and for any purchases you choose to make later.'
              }
              show={show}
            >
              <fieldset
                disabled={!disclosureAcknowledged}
                aria-disabled={!disclosureAcknowledged}
                className={
                  disclosureAcknowledged
                    ? 'transition-opacity'
                    : 'pointer-events-none opacity-40 transition-opacity'
                }
              >
                <OnboardingFormPayment setShow={setShow} show={show} />
              </fieldset>
            </OnboardingFormStep>
          )}

          <OnboardingFormStep
            step={addressStep}
            title="Shipping Address"
            description="Where can we send your retainers? Make sure to add your apartment
          number if you have one."
            show={show}
          >
            <OnboardingFormAddress setShow={setShow} show={show} />
          </OnboardingFormStep>
          <Transition
            as="div"
            show={show}
            className="flex flex-col gap-2 border-t pt-4"
          >
            <div className="flex flex-col gap-3 transition-opacity duration-150 data-closed:opacity-0 data-enter:opacity-100 data-leave:opacity-0">
              {/* Same geometry as CheckboxInput below so the boxes line up. */}
              <div className="relative flex gap-x-3">
                <div className="flex h-6 items-center">
                  <input
                    id="onboarding-consent-all"
                    type="checkbox"
                    className="size-4 rounded border-rcgray-600 text-rcprimary-400 focus:ring-rcprimary-400"
                    checked={allConsentsChecked}
                    onChange={toggleAllConsents}
                  />
                </div>
                <label
                  htmlFor="onboarding-consent-all"
                  className="font-semibold text-black/90 text-sm leading-6"
                >
                  {SELECT_ALL_LABEL}
                </label>
              </div>
              <div className="flex flex-col gap-3">
                <CheckboxInput
                  name="userAttributes.acceptTerms"
                  label={TERMS_LABEL}
                  required
                  description={
                    <TermsDescription onOpenPolicy={setPolicyAndOpenModal} />
                  }
                />
                <CheckboxInput
                  name="userAttributes.acceptSms"
                  label={SMS_LABEL}
                  description={<SmsDescription />}
                />
                <CheckboxInput
                  name="userAttributes.acceptMarketing"
                  label={MARKETING_LABEL}
                  description={<MarketingDescription />}
                />
              </div>
            </div>
          </Transition>
        </>
      ) : (
        <div className="absolute top-0 left-0 h-[400px] w-full bg-gray-50 sm:rounded-lg sm:shadow-2xl">
          {shouldRender}
        </div>
      )}

      <OnboardingFormErrors mutationErrors={mutationErrors} error={error} />

      {!masterLoading && (
        <Transition show={show}>
          <div className="flex flex-col gap-2 transition-opacity duration-150 data-closed:opacity-0 data-enter:opacity-100 data-leave:opacity-0">
            {isReferralAlreadyClaimed && (
              <div
                className="flex items-center gap-3 rounded-lg border border-red-500 bg-red-50 p-3 font-medium text-base text-red-800"
                role="alert"
              >
                <ExclamationTriangleIcon
                  aria-hidden
                  className="size-8 shrink-0 text-red-600"
                />
                <p className="text-center">{referralAlreadyClaimedMessage}</p>
                <ExclamationTriangleIcon
                  aria-hidden
                  className="size-8 shrink-0 text-red-600"
                />
              </div>
            )}
            {isReferralUnavailable && (
              <div
                className="flex items-center gap-3 rounded-lg border border-red-500 bg-red-50 p-3 font-medium text-base text-red-800"
                role="alert"
              >
                <ExclamationTriangleIcon
                  aria-hidden
                  className="size-8 shrink-0 text-red-600"
                />
                <p className="text-center">{referralUnavailableMessage}</p>
                <ExclamationTriangleIcon
                  aria-hidden
                  className="size-8 shrink-0 text-red-600"
                />
              </div>
            )}
            <SubmitButton
              disabled={
                !isFormValid ||
                isReferralAlreadyClaimed ||
                isReferralUnavailable
              }
              full
              isSubmitting={isSubmitting}
              isLoading={isLoading}
              label="Let's Get Started!"
              notRounded
            />
            {/* Below the button, as in the consent gate, so it reads as a
                closing note rather than part of the last consent. */}
            <p className="text-center text-black/50 text-xs">
              {PREFERENCES_FOOTER}
            </p>
          </div>
        </Transition>
      )}
    </>
  );
}

OnboardingFormInner.propTypes = {
  error: PropTypes.object,
  isReferralAlreadyClaimed: PropTypes.bool,
  isReferralUnavailable: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  mutationErrors: PropTypes.object,
  pError: PropTypes.object,
  rError: PropTypes.object,
};

export default OnboardingFormInner;
